/**
 * Componente de seleção de data.
 */

import { FiCalendar } from "react-icons/fi";
import { maxDateYMD, todayYMD } from "../utils";

const EMBEDDED_VISIBLE_DATES = 7;

interface DatePickerProps {
  date: string;
  availableDates: string[];
  onDateChange: (date: string) => void;
  onNextDate: () => void;
  embedded?: boolean;
}

export function DatePicker({
  date,
  availableDates,
  onDateChange,
  onNextDate,
  embedded = false,
}: DatePickerProps) {
  const visibleLimit = embedded ? EMBEDDED_VISIBLE_DATES : 20;
  const selectedIndex = availableDates.indexOf(date);
  const safeSelectedIndex = selectedIndex >= 0 ? selectedIndex : 0;
  const windowStart = embedded
    ? Math.min(
        Math.max(0, safeSelectedIndex - (visibleLimit - 1)),
        Math.max(0, availableDates.length - visibleLimit)
      )
    : 0;
  const visibleDates = availableDates.slice(windowStart, windowStart + visibleLimit);
  const canGoPrevious = embedded && windowStart > 0;
  const canGoNext =
    embedded &&
    availableDates.length > 0 &&
    safeSelectedIndex < availableDates.length - 1;

  const handleDateInputChange = (newDate: string) => {
    if (availableDates.length === 0 || availableDates.includes(newDate)) {
      onDateChange(newDate);
    }
  };

  const handlePreviousDate = () => {
    if (!canGoPrevious) return;
    const previousIndex =
      selectedIndex > 0 ? selectedIndex - 1 : Math.max(0, windowStart - 1);
    onDateChange(availableDates[previousIndex]);
  };

  return (
    <section
      className={
        embedded
          ? ""
          : "rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/70 sm:p-6"
      }
    >
      {!embedded && (
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <FiCalendar className="h-5 w-5 text-primary-700" aria-hidden="true" />
              <h2 className="text-xl font-bold tracking-tight text-slate-950">
                Escolha a data
              </h2>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Veja os próximos dias com disponibilidade para este atendimento.
            </p>
          </div>
        </div>
      )}

      {availableDates.length > 0 && (
        <div className="mb-4">
          {embedded && (
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-slate-500">
                Mostrando datas com disponibilidade. Use as setas ou escolha pelo
                calendário.
              </p>
              <label className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                <FiCalendar className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                <span className="sr-only">Escolher outra data</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => handleDateInputChange(e.target.value)}
                  min={availableDates[0] || todayYMD()}
                  max={availableDates[availableDates.length - 1] || maxDateYMD()}
                  className="min-w-0 bg-transparent text-sm font-semibold text-slate-700 outline-none"
                />
              </label>
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {embedded && (
              <button
                type="button"
                onClick={handlePreviousDate}
                disabled={!canGoPrevious}
                className="min-w-[52px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xl text-slate-500 transition hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Data anterior"
              >
                <span aria-hidden>‹</span>
              </button>
            )}

            {visibleDates.map((d) => {
              const isSelected = d === date;
              const dateObj = new Date(d + "T00:00:00");
              const dayName = dateObj.toLocaleDateString("pt-BR", { weekday: "short" });
              const dayNum = dateObj.getDate();
              const monthName = dateObj.toLocaleDateString("pt-BR", { month: "short" });

              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => onDateChange(d)}
                  className={`min-w-[92px] rounded-xl border px-3 py-2 text-center transition focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
                    isSelected
                      ? "border-primary-600 bg-gradient-to-r from-primary-600 to-primary-700 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-primary-300"
                  }`}
                  aria-pressed={isSelected}
                >
                  <div className="text-xs font-semibold capitalize">{dayName}</div>
                  <div className="mt-0.5 text-lg font-bold leading-none">{dayNum}</div>
                  <div className="text-xs capitalize">{monthName}</div>
                </button>
              );
            })}

            {embedded && (
              <button
                type="button"
                onClick={onNextDate}
                disabled={!canGoNext}
                className="min-w-[52px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xl text-slate-500 transition hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Próxima data"
              >
                <span aria-hidden>›</span>
              </button>
            )}
          </div>
        </div>
      )}

      {!embedded && (
        <div className="mb-2">
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            Ou selecione outra data
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              handleDateInputChange(e.target.value);
            }}
            min={availableDates.length > 0 ? availableDates[0] : todayYMD()}
            max={
              availableDates.length > 0
                ? availableDates[availableDates.length - 1]
                : maxDateYMD()
            }
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition focus:border-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100"
          />
        </div>
      )}
    </section>
  );
}
